import type { State } from "@/features/core/store";
import { displayedSolverStartPoint } from "@/features/problem/selectors";
import type { ViewportRenderSnapshot } from "@/features/viewport/types";
import { iterateHeightOrFloor } from "../helpers/iteratePositions";
import { PointCloud } from "../helpers/pointCloud";
import { PALETTE, SPRITE } from "../palette";
import { ZScaledLayer } from "./base/LayerBase";

// Subtle draggable marker for where IPM/PDHG/primal-simplex begin iterating:
// a small gray ring at the effective start point (see
// displayedSolverStartPoint for the applicability/snapping rules). In 3D it
// rides at the first iterate's render height — the per-solver convergence
// lift (mu for IPM, scaled eps for PDHG, zero for simplex) — so it stays
// attached to the start of the path at any zScale; in 2D everything flattens
// to the floor. Dragging maps through the z = 0 plane like the vertices.
// Unlike the other iterate layers it does not hide during the 2D/3D
// transition.
export class SolverStartLayer extends ZScaledLayer {
  readonly slot = "solverStart" as const;
  private readonly cloud = this.own(new PointCloud({ color: PALETTE.solverStart, opacity: 0.9, pixelSize: SPRITE.solverStart, shape: "ring", slot: this.slot, vertexColors: false }));
  readonly object3D = this.cloud.object3D;
  override readonly invalidationKeys = ["iterate"] as const;

  // shown through the 2D/3D transition too, unlike the other iterate layers
  protected override visibleIn(): boolean {
    return true;
  }

  protected dependencies(state: State, _snap: ViewportRenderSnapshot): readonly unknown[] {
    return [
      state.solverStartPoint,
      state.solverMode,
      state.solverSettings.simplexDualMode,
      state.solverSettings.activeSetMethod,
      state.solverSettings.activeSetIndexOrder,
      state.vertices,
      state.completionMode,
      state.objectiveVector,
      state.currentObjective,
      state.polytope,
      state.iteratePath,
    ];
  }

  protected rebuild(state: State, _snap: ViewportRenderSnapshot): void {
    const point = displayedSolverStartPoint(state);
    if (!point) {
      this.hide();
      return;
    }
    this.cloud.draw(1, (pos) => pos.set([point[0], point[1], iterateHeightOrFloor(state.iteratePath, 0)]));
  }
}
